import MultiSelect from "../coreui/multi-select"

const syncingSelects = new WeakSet()

function valuesFor(select) {
  return Array.from(select.selectedOptions, (option) => String(option.value))
}

function sameValues(left, right) {
  return left.length === right.length && left.every((value, index) => value === right[index])
}

function withCoreUISyncSuppressed(select, callback) {
  syncingSelects.add(select)
  try {
    return callback()
  } finally {
    syncingSelects.delete(select)
  }
}

// A cleared single select must still submit an empty scalar to Rails, including
// fields that originally had no include_blank option.
export function selectEmptyOption(select) {
  let blank = Array.from(select.options).find((option) => option.value === "")
  if (!blank) {
    blank = new Option("", "")
    select.append(blank)
  }
  blank.selected = true
}

// Remount through the stable Stimulus owner after native option changes,
// retaining Rails reset defaults and disposing dropdown listeners.
export function syncMultiSelect(select) {
  if (!MultiSelect.getInstance(select)) return

  withCoreUISyncSuppressed(select, () => {
    select.dispatchEvent(new CustomEvent("coreui:multi-select-refresh"))
  })
}

export function isMultiSelectSyncing(select) {
  return syncingSelects.has(select)
}

function notifyIfChanged(select, previousValues) {
  const nextValues = valuesFor(select)
  if (!sameValues(previousValues, nextValues)) {
    select.dispatchEvent(new Event("change", { bubbles: true }))
  }
}

export function replaceOptions(select, options, { preserveSelection = false } = {}) {
  const previousValues = valuesFor(select)
  const selectedValues = preserveSelection ? new Set(previousValues) : new Set()
  const fragment = document.createDocumentFragment()
  let hasExplicitSelection = false

  const append = (items, parent) => items.forEach((item) => {
    if (item.options) {
      const group = document.createElement("optgroup")
      group.label = item.label
      group.disabled = Boolean(item.disabled)
      append(item.options, group)
      parent.append(group)
      return
    }
    const option = document.createElement("option")
    option.value = String(item.value)
    option.textContent = String(item.text ?? item.label ?? item.value)
    option.disabled = Boolean(item.disabled)

    const selected = selectedValues.has(option.value) || (!preserveSelection && Boolean(item.selected))
    option.selected = selected
    option.defaultSelected = Boolean(item.defaultSelected ?? item.selected)
    hasExplicitSelection ||= selected
    parent.append(option)
  })
  append(options, fragment)

  select.replaceChildren(fragment)
  if (!hasExplicitSelection) {
    if (select.multiple) select.selectedIndex = -1
    else selectEmptyOption(select)
  }
  syncMultiSelect(select)
  notifyIfChanged(select, previousValues)
}

export function setSelectedValues(select, values) {
  const previousValues = valuesFor(select)
  const normalizedValues = values === null || values === undefined
    ? []
    : Array.isArray(values) ? values : [values]
  const selectedValues = new Set(normalizedValues.map(String))
  let selectedSingleValue = false

  for (const option of select.options) {
    const selected = selectedValues.has(String(option.value)) && (select.multiple || !selectedSingleValue)
    option.selected = selected
    selectedSingleValue ||= selected
  }

  if (!selectedSingleValue && !select.multiple) {
    selectEmptyOption(select)
  }
  syncMultiSelect(select)
  notifyIfChanged(select, previousValues)
}

export function selectAll(select) {
  const values = Array.from(select.options)
    .filter((option) => !option.disabled && !option.closest("optgroup")?.disabled && option.value !== "")
    .map((option) => option.value)
  setSelectedValues(select, select.multiple ? values : values.slice(0, 1))
}

export function setDisabled(select, disabled) {
  const isDisabled = Boolean(disabled)
  select.disabled = isDisabled
  select.classList.toggle("disabled", isDisabled)
  syncMultiSelect(select)
}

function selectedOptionData(select) {
  return Array.from(select.selectedOptions, (option) => ({
    value: option.value,
    text: option.textContent,
    selected: true,
    disabled: option.disabled,
    defaultSelected: option.defaultSelected
  }))
}

function resultsAt(payload, key) {
  return key.split(".").reduce((value, part) => value?.[part], payload)
}

export function configureRemoteSearch(select, {
  url,
  resultsKey,
  valueField,
  labelField,
  debounce = 200
}) {
  let timeout
  let requestController
  let requestSequence = 0

  let restoringSearch = false

  // A response to an earlier query must not remount the input under an IME.
  const cancelPendingSearch = () => {
    requestSequence += 1
    clearTimeout(timeout)
    requestController?.abort()
  }
  select.addEventListener("coreui:multi-select-composition-start", cancelPendingSearch)

  const search = () => {
    if (restoringSearch) return

    const searchInput = select.parentElement?.querySelector(".form-multi-select-search")
    const query = searchInput?.value.trim() || ""
    const sequence = ++requestSequence

    clearTimeout(timeout)
    requestController?.abort()
    if (!query) return

    timeout = setTimeout(async () => {
      requestController = new AbortController()

      try {
        const requestUrl = new URL(url, window.location.origin)
        requestUrl.searchParams.set("q", query)
        const response = await fetch(requestUrl, {
          credentials: "same-origin",
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest"
          },
          signal: requestController.signal
        })
        if (!response.ok) throw new Error(`Remote select request failed (${response.status})`)

        const payload = await response.json()
        if (sequence !== requestSequence) return

        const selected = selectedOptionData(select)
        const selectedValues = new Set(selected.map((option) => String(option.value)))
        const remoteOptions = Array.from(resultsAt(payload, resultsKey) || [], (item) => ({
          value: item[valueField],
          text: item[labelField],
          disabled: item.disabled
        })).filter((option) => option.value !== null && option.value !== undefined)
        const options = selected.concat(remoteOptions.filter((option) => !selectedValues.has(String(option.value))))

        replaceOptions(select, options, { preserveSelection: true })

        const instance = MultiSelect.getInstance(select)
        const nextSearchInput = select.parentElement?.querySelector(".form-multi-select-search")
        if (instance && nextSearchInput) {
          nextSearchInput.value = query
          try {
            restoringSearch = true
            instance.filter(query)
          } finally {
            restoringSearch = false
          }
          instance.show()
          nextSearchInput.focus()
        }
      } catch (error) {
        if (error.name !== "AbortError") console.error(error)
      }
    }, debounce)
  }

  select.addEventListener("search.coreui.multi-select", search)

  return () => {
    cancelPendingSearch()
    select.removeEventListener("coreui:multi-select-composition-start", cancelPendingSearch)
    select.removeEventListener("search.coreui.multi-select", search)
  }
}
